import { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { format, subDays } from "date-fns";
import { config, encodeBase64RequestBody, priceFormat, numberFormat, slugFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import {
  applyCurrencyConversion,
  recalculateItemTotals,
  calculateQuotationSubtotal,
  calculateQuotationCostTotal,
  calculateQuotationTotals,
} from "./utils/quotationCalculations";

const QuotationEdit = () => {
  const { id_quotation } = useParams();
  const [ companyList, setCompanyList ] = useState(null);
  const [ subcompanyList, setSubcompanyList ] = useState(null);
  const [ productList, setProductList ] = useState(null);
  const [ domainList, setDomainList ] = useState([]);
  const [ categoryList, setCategoryList ] = useState([]);
  const [ paymentSimulationList, setPaymentSimulationList ] = useState(null);
  const [ companyId, setCompanyId ] = useState(null);
  const [ subcompanyId, setSubcompanyId ] = useState(null);
  const [ quotationCurrency, setQuotationCurrency ] = useState('IDR');
  const [ quotationExchangeRate, setQuotationExchangeRate ] = useState(16000);
  const [ quotationExchangeFloating, setQuotationExchangeFloating ] = useState(true);
  const [ quotationCreatedDate, setQuotationCreatedDate ] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [ quotationExpiredDate, setQuotationExpiredDate ] = useState(format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'));
  const [ quotationSubtotal, setQuotationSubtotal ] = useState(0);
  const [ quotationDiscountPrice, setQuotationDiscountPrice ] = useState(0);
  const [ quotationDiscountRate, setQuotationDiscountRate ] = useState(0);
  const [ quotationTotal, setQuotationTotal ] = useState(0);
  const [ quotationCostTotal, setQuotationCostTotal ] = useState(0);
  const [ quotationMarginTotal, setQuotationMarginTotal ] = useState(0);
  const [ quotationMarginRate, setQuotationMarginRate ] = useState(0);
  const [ quotationBillingLongPeriod, setQuotationBillingLongPeriod ] = useState(1);
  const [ quotationBillingPaymentTerm, setQuotationBillingPaymentTerm ] = useState('Full Payment');
  const [ quotationBillingPaymentPeriod, setQuotationBillingPaymentPeriod ] = useState('One Time');
  const [ quotationCompanyAddress, setQuotationCompanyAddress ] = useState('');
  const [ quotationSubscriptionTerm, setQuotationSubscriptionTerm ] = useState('');
  const [ quotationPaymentTerm, setQuotationPaymentTerm ] = useState('');
  const [ quotationCancellationTerm, setQuotationCancellationTerm ] = useState('');
  const [ quotationItemList, setQuotationItemList ] = useState([]);
  const [ quotationBundleList, setQuotationBundleList ] = useState([]);
  const [ quotationPaymentOption, setQuotationPaymentOption ] = useState(100);
  const [ hasCompanyLicense, setHasCompanyLicense ] = useState(false);
  const [ activeLicenceDate, setActiveLicenceDate ] = useState(null);
  const [ activePaymentPeriod, setActivePaymentPeriod ] = useState(null);
  const [ subscriptionTerm, setSubscriptionTerm ] = useState(null);
  const [ paymentTerm, setPaymentTerm ] = useState(null);
  const [ cancellationTerm, setCancellationTerm ] = useState(null);
  const [ paymentList, setPaymentList ] = useState([]);
  const [ isInvoicingOnly, setIsInvoicingOnly ] = useState(false);
  const [ isLoadingSubmit, setIsLoadingSubmit ] = useState(false);
  
  const navigate = useNavigate();

  const productTaggingOptions = [
    { label: 'GWS SMB', value: 'GWS SMB' },
    { label: 'GWS CORP', value: 'GWS CORP' },
    { label: 'GWS ENTERPRISE', value: 'GWS ENTERPRISE' },
    { label: 'CHROME ENTERPRISE', value: 'CHROME ENTERPRISE' },
    { label: 'CHROME EDUCATION', value: 'CHROME EDUCATION' },
    { label: 'HARDWARE', value: 'HARDWARE' },
    { label: 'EDUCATION LICENSES', value: 'EDUCATION LICENSES' },
    { label: 'TRAINING', value: 'TRAINING' },
    { label: 'SERVICES', value: 'SERVICES' },
    { label: 'MICROSOFT', value: 'MICROSOFT' },
    { label: 'GOOGLE CLOUD', value: 'GOOGLE CLOUD' },
    { label: 'GEMINI ENTERPRISE', value: 'GEMINI ENTERPRISE' },
    { label: 'APIGEE', value: 'APIGEE' },
    { label: 'LUMAPPS', value: 'LUMAPPS' },
    { label: 'JUMPCLOUD', value: 'JUMPCLOUD' },
    { label: 'OTHER LICENSES', value: 'OTHER LICENSES' },
    { label: 'CUSTOM APPS', value: 'CUSTOM APPS' },
    { label: 'KAMIGAWE LICENSES', value: 'KAMIGAWE LICENSES' },
    { label: 'KAMIGAWE SERVICES', value: 'KAMIGAWE SERVICES' },
    { label: 'SAAS APPS (ALBAN)', value: 'SAAS APPS (ALBAN)' }
  ];
  const paymentTermOptions = [
    { label: 'Floating', value: `Payments shall be calculated based on the JISDOR on the invoice issuance date.\nThe total invoiced amount must be received in full and prior to the due date. Any bank charges or fees incurred by the customer's bank shall be the sole responsibility of the customer.\nTaxes shall be applied at the prevailing rate on the invoice issuance date. If any additional tax applies, the customer must notify Polarisweb (PointStar) in writing at the time of payment.\nThe customer shall provide proof of payment and ensure the invoice number is clearly referenced in the transaction details.\nFailure to make timely payments may result in the suspension of services without prior notice, and the Company reserves the right to impose penalties or take further legal action to recover outstanding amounts.\nThese payment terms shall be governed by and construed in accordance with the applicable laws and regulations of the governing jurisdiction.\nAny disputes arising from these terms shall be resolved through good faith negotiations. If no resolution is reached, the matter shall be settled in accordance with the dispute resolution mechanism agreed upon by both parties.` },
    { label: 'Flat Rate', value: `Payments shall be calculated based on the pre-agreed fixed exchange rate, as stated in the invoice or contractual agreement.\nThe total invoiced amount must be received in full and prior to the due date. Any bank charges or fees incurred by the customer's bank shall be the sole responsibility of the customer.\nTaxes shall be applied at the prevailing rate on the invoice issuance date. If any additional tax applies, the customer must notify the Company in writing at the time of payment.\nThe customer shall provide proof of payment and ensure the invoice number is clearly referenced in the transaction details.\nFailure to make timely payments may result in the suspension of services without prior notice. The Company reserves the right to impose penalties, charge interest on overdue amounts, or take further legal action as deemed necessary.\nThese payment terms shall be governed by and construed in accordance with the applicable laws and regulations of the governing jurisdiction.\nAny disputes arising from these terms shall be resolved through good faith negotiations. If no resolution is reached, the matter shall be settled in accordance with the dispute resolution mechanism agreed upon by both parties.` },
  ];
  const cancellationTermOptions = [
    { label: 'General', value: `Submit a formal written notice of termination via email or a signed letter from the highest-ranking officer of the Customer’s organization. Termination shall only take effect upon PointStar’s written acknowledgment and completion of all necessary discussions and settlement processes.\nFulfill all outstanding payment obligations for Subscriptions: If termination occurs prior to activation, no payment shall be required. If the subscription has been activated, the Customer shall remain liable for 100% of the total subscription fee.\nFulfill all outstanding payment obligations for Services: If termination occurs before service execution, the Customer shall pay 30% of the total agreed price. If services are in progress or have been completed, 100% of the total agreed price shall be due.\nReturn all Confidential Information belonging to PointStar that is in the Customer's possession or control.\nIndemnify and hold harmless PointStar against any claims, damages, losses, liabilities, costs, or expenses (including reasonable legal fees) arising from or in connection with the termination of this Agreement.` },
  ];

  useEffect(() => {
    getCompanyList();
    getProductList();
    getCategoryList();
    getPaymentSimulationList();
  }, []);

  useEffect(() => {
    if (companyList) {
      getQuotation();
    }
  }, [companyList]);
  
  useEffect(() => {
    if (companyId) {
      getCompanyDomains();
    }
  }, [companyId]);

  // useEffect(() => {
  //   setSubcompanyList(null);
  //   setSubcompanyId(null);
  //   setQuotationItemList([]);
  //   if (companyId) {
  //     const company = companyList.find(company => company.id_company === companyId.value);
  //     setDomainList([company.company_domain]);
  //     getSubcompanyList();
  //   }
  // }, [companyId]);

  // useEffect(() => {
  //   if (subcompanyList) {
  //     const domains = [];
  //     subcompanyList.forEach(subcompany => {
  //       domains.push(subcompany.company_domain);
  //     });
  //   }
  // }, [subcompanyList]);

  // useEffect(() => {
  //   if (subcompanyId) {
  //     const subcompany = subcompanyList.find(company => company.id_subcompany === subcompanyId.value);
  //     setQuotationItemList([]);
  //     setQuotationCompanyAddress([subcompany.company_billing_address]);
  //   }
  // }, [subcompanyId]);

  useEffect(() => {
    const subtotal = calculateQuotationSubtotal(quotationItemList);
    const totalCost = calculateQuotationCostTotal(quotationItemList);
    setQuotationSubtotal(subtotal);
    setQuotationCostTotal(totalCost);
    if (quotationItemList.length === 0) {
      addQuotationItem();
    }
    for (let i in quotationBundleList) {
      let subtotal = 0;
      for (let j in quotationBundleList[i].bundle_items) {
        const itemIndex = quotationItemList.findIndex(item => item.product_id === quotationBundleList[i].bundle_items[j].value);
        if (itemIndex !== -1) {
          quotationBundleList[i].bundle_items[j].total = parseFloat((quotationItemList[itemIndex].product_total).toFixed(3));
          subtotal += quotationBundleList[i].bundle_items[j].total;
        } else {
          quotationBundleList[i].bundle_items.splice(j, 1);
        }
      }
      quotationBundleList[i].bundle_subtotal = subtotal;
      setQuotationBundleList([...quotationBundleList]);
    }
  }, [quotationItemList]);

  useEffect(() => {
    for (let i in quotationItemList) {
      quotationItemList[i].product_currency = quotationCurrency;
      const { product_cost, product_price } = applyCurrencyConversion(quotationItemList[i], quotationCurrency, quotationExchangeRate);
      quotationItemList[i].product_cost = product_cost;
      quotationItemList[i].product_price = product_price;
      Object.assign(quotationItemList[i], recalculateItemTotals(quotationItemList[i]));
    }
    setQuotationItemList([...quotationItemList]);
  }, [quotationCurrency, quotationExchangeRate]);

  useEffect(() => {
    if (quotationSubtotal > 0) {
      const totalCost = calculateQuotationCostTotal(quotationItemList);
      const { quotationTotal, quotationMarginTotal, quotationMarginRate } = calculateQuotationTotals(
        quotationItemList,
        quotationSubtotal,
        quotationDiscountPrice
      );
      setQuotationTotal(quotationTotal);
      setQuotationCostTotal(totalCost);
      setQuotationMarginTotal(quotationMarginTotal);
      setQuotationMarginRate(quotationMarginRate);
    }
  }, [quotationSubtotal, quotationDiscountPrice, quotationCostTotal]);

  useEffect(() => {
    if (quotationBillingPaymentTerm === 'Full Payment') {
      setQuotationBillingPaymentPeriod('One Time');
    }
    if (hasCompanyLicense) {
      setQuotationBillingPaymentPeriod(activePaymentPeriod);
    }
  }, [quotationBillingPaymentTerm]);

  useEffect(() => {
    if (quotationBillingPaymentPeriod === 'Custom') {
      addPayment();
    } else {
      setPaymentList([]);
      switch (quotationBillingPaymentPeriod) {
        case 'Monthly': setQuotationBillingLongPeriod(1); break;
        case 'Bimonthly': setQuotationBillingLongPeriod(2); break;
        case 'Quarterly': setQuotationBillingLongPeriod(3); break;
        case 'Trimesterly': setQuotationBillingLongPeriod(4); break;
        case 'Semesterly': setQuotationBillingLongPeriod(6); break;
        case 'Annually': setQuotationBillingLongPeriod(12); break;
        default: setQuotationBillingLongPeriod(1); break;
      }
    }
  }, [quotationBillingPaymentPeriod]);

  useEffect(() => {
    let totalPaymentRate = paymentList.reduce((sum, payment) => sum + parseFloat(payment.payment_rate), 0);
    const updatedPaymentList = paymentList.map((payment, index) => {
      const error_rate = parseFloat((totalPaymentRate).toFixed(0)) !== 100 || payment.payment_rate === 0;
      const error_date = paymentList.some(
        (otherPayment, otherIndex) =>
          otherIndex !== index && otherPayment.start_term_period === payment.start_term_period
      );
      return { ...payment, error_rate, error_date };
    });

    const hasChanged = updatedPaymentList.some((payment, index) =>
      payment.error_rate !== paymentList[index].error_rate ||
      payment.error_date !== paymentList[index].error_date
    );
    if (hasChanged) {
      setPaymentList(updatedPaymentList);
    }
  }, [paymentList]);

  const getQuotation = async () => {
    try {
      const requestQuotation = {
        'id_quotation': id_quotation
      };
      const fetchQuotation = await jwtFetch(config.ENDPOINT + 'get/quotation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestQuotation)
      });
      const responseQuotation = await fetchQuotation.json();
      if (responseQuotation.status === 200) {
        setCompanyId({ value: responseQuotation.data[0].company_id, label: responseQuotation.data[0].company_name });
        setSubcompanyId({ value: responseQuotation.data[0].subcompany_id, label: responseQuotation.data[0].subcompany_name });
        setQuotationCurrency(responseQuotation.data[0].quotation_currency);
        setQuotationExchangeRate(responseQuotation.data[0].quotation_exchange_rate);
        setQuotationExchangeFloating(responseQuotation.data[0].quotation_exchange_floating === 'Floating' ? true : false);
        setQuotationCreatedDate(format(new Date(responseQuotation.data[0].created_on), 'yyyy-MM-dd'));
        setQuotationExpiredDate(format(new Date(responseQuotation.data[0].quotation_expired_date), 'yyyy-MM-dd'));
        setQuotationSubtotal(responseQuotation.data[0].quotation_subtotal);
        setQuotationDiscountPrice(parseFloat(responseQuotation.data[0].quotation_discount_price));
        setQuotationDiscountRate(parseFloat(responseQuotation.data[0].quotation_discount_rate));
        setQuotationTotal(responseQuotation.data[0].quotation_total);
        setQuotationCostTotal(responseQuotation.data[0].quotation_cost);
        setQuotationMarginTotal(responseQuotation.data[0].quotation_margin_price);
        setQuotationMarginRate(responseQuotation.data[0].quotation_margin_rate);
        setQuotationBillingLongPeriod(responseQuotation.data[0].quotation_item_list[0].billing_long_period);
        setQuotationBillingPaymentTerm(responseQuotation.data[0].quotation_item_list[0].billing_payment_term);
        setQuotationBillingPaymentPeriod(responseQuotation.data[0].quotation_item_list[0].billing_payment_period);
        setQuotationSubscriptionTerm(responseQuotation.data[0].subscription_terms);
        setQuotationPaymentTerm(responseQuotation.data[0].payment_terms);
        setQuotationCancellationTerm(responseQuotation.data[0].cancellation_terms);
        setIsInvoicingOnly(responseQuotation.data[0].is_invoicing_only === 1 ? true : false);
        const itemList = [];
        for (let i in responseQuotation.data[0].quotation_item_list) {
          const product = await getProduct(responseQuotation.data[0].quotation_item_list[i].product_id);
          const item = {
            product: { value: product.id_product, label: product.product_name },
            product_id: responseQuotation.data[0].quotation_item_list[i].product_id,
            product_name: product.product_name,
            product_display_name: responseQuotation.data[0].quotation_item_list[i].product_display_name,
            product_display_description: responseQuotation.data[0].quotation_item_list[i].product_display_description,
            product_type: responseQuotation.data[0].quotation_item_list[i].product_type,
            product_category: product.category_name,
            product_domain: responseQuotation.data[0].quotation_item_list[i].product_domain,
            product_currency_original: responseQuotation.data[0].quotation_item_list[i].product_currency_original,
            product_currency: responseQuotation.data[0].quotation_item_list[i].product_currency,
            product_cost_original: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_cost_original),
            product_cost: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_cost),
            product_price_original: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_price_original),
            product_price: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_price),
            product_qty: responseQuotation.data[0].quotation_item_list[i].product_qty,
            product_subtotal: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_subtotal),
            product_discount_price: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_discount_price),
            product_discount_rate: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_discount_rate),
            product_total: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_total),
            product_margin_price: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_margin_price),
            product_margin_rate: parseFloat(responseQuotation.data[0].quotation_item_list[i].product_margin_rate),
            product_tagging: responseQuotation.data[0].quotation_item_list[i].product_tagging,
            service_type: responseQuotation.data[0].quotation_item_list[i].service_type,
            is_upgrade: responseQuotation.data[0].quotation_item_list[i].is_upgrade,
            contract_long_period: responseQuotation.data[0].quotation_item_list[i].contract_long_period,
            contract_duration_period: responseQuotation.data[0].quotation_item_list[i].contract_duration_period,
          }
          itemList.push(item);
        }
        setQuotationItemList(itemList);
        const bundleList = [];
        for (let i in responseQuotation.data[0].quotation_items.filter(item => item.product_type === 'Bundle')) {
          let bundle = {
            bundle_code: slugFormat(responseQuotation.data[0].quotation_items[i].product_display_name),
            bundle_display_name: responseQuotation.data[0].quotation_items[i].product_display_name,
            bundle_display_description: responseQuotation.data[0].quotation_items[i].product_display_description,
            bundle_qty: responseQuotation.data[0].quotation_items[i].product_qty,
            bundle_subtotal: parseFloat(responseQuotation.data[0].quotation_items[i].product_subtotal),
            bundle_items: [],
          };
          for (let j in responseQuotation.data[0].quotation_item_list.filter(itemList => itemList.quotation_item_id === responseQuotation.data[0].quotation_items[i].id_quotation_item)) {
            const bundleItem = {
              value: responseQuotation.data[0].quotation_item_list[j].product_id, 
              label: responseQuotation.data[0].quotation_item_list[j].product_display_name, 
              total: parseFloat(responseQuotation.data[0].quotation_item_list[j].product_total),
            }
            bundle.bundle_items.push(bundleItem);
          }
          bundleList.push(bundle);
        }
        setQuotationBundleList(bundleList);

        const payments = new Map();
        let paymentCustom = false;
        for (let i in responseQuotation.data[0].quotation_items) {
          if (responseQuotation.data[0].quotation_items[i].billing_payment_period === 'Custom') {
            paymentCustom = true;
            for (let j in responseQuotation.data[0].quotation_items[i].payment_simulations) {
              const key = responseQuotation.data[0].quotation_items[i].payment_simulations[j].term_number;            
              if (payments.has(key)) {
                const existing = payments.get(key);
                payments.set(key, {
                  ...existing,
                  payment_amount_exc_tax: (parseFloat(existing.payment_amount_exc_tax) + parseFloat(responseQuotation.data[0].quotation_items[i].payment_simulations[j].payment_amount_exc_tax)).toFixed(3)
                });
              } else {
                const paymentRate = parseFloat((
                  responseQuotation.data[0].quotation_items[i].payment_simulations[j].payment_amount_exc_tax / 
                  responseQuotation.data[0].quotation_total * 100
                ).toFixed(3));

                payments.set(key, {
                  number_of_months: responseQuotation.data[0].quotation_items[i].payment_simulations[j].number_of_months,
                  start_term_period: format(responseQuotation.data[0].quotation_items[i].payment_simulations[j].start_term_period, 'yyyy-MM-dd'),
                  end_term_period: format(responseQuotation.data[0].quotation_items[i].payment_simulations[j].end_term_period, 'yyyy-MM-dd'),
                  payment_amount_exc_tax: parseFloat(responseQuotation.data[0].quotation_items[i].payment_simulations[j].payment_amount_exc_tax).toFixed(3),
                  payment_amount_inc_tax: 0,
                  payment_rate: paymentRate,
                  error_rate: false,
                  error_date: false
                });
              }
            }
          }
        }
        if (paymentCustom) {
          setPaymentList(Array.from(payments.values()));
        }

      } else {
        alert(responseQuotation.message);
      }
    } catch (error) {
      console.error('Error in getQuotation:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getCompanyList = async () => {
    try {
      const requestCompanyList = {};
      const fetchCompanyList = await jwtFetch(config.ENDPOINT + 'get/company', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyList)
      });
      const responseCompanyList = await fetchCompanyList.json();
      if (responseCompanyList.status === 200) {
        setCompanyList(responseCompanyList.data.filter(company => company.company_status === 'Active').sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '')));
      } else {
        alert(responseCompanyList.message);
      }
    } catch (error) {
      console.error('Error in getCompanyList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getProduct = async (id_product) => {
    try {
      const requestProduct = {
        'id_product': id_product
      };
      const fetchProduct = await jwtFetch(config.ENDPOINT + 'get/product', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestProduct)
      });
      const responseProduct = await fetchProduct.json();
      if (responseProduct.status === 200) {
        return responseProduct.data[0];
      } else {
        alert(responseProduct.message);
      }
    } catch (error) {
      console.error('Error in getProduct:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getSubcompanyList = async () => {
    try {
      const requestSubcompanyList = {
        company_id: companyId.value
      }
      const fetchSubcompanyList = await jwtFetch(config.ENDPOINT + 'get/subcompany', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: encodeBase64RequestBody(requestSubcompanyList)
      });
      const responseSubcompanyList = await fetchSubcompanyList.json();
      if (responseSubcompanyList.status === 200) {
        setSubcompanyList(responseSubcompanyList.data.filter(subcompany => subcompany.company_status === 'Active').sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '')));
      } else {
        alert(responseSubcompanyList.message);
      }
    } catch (error) {
      console.error('Error in getSubcompanyList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getProductList = async () => {
    try {
      const requestProductList = {
        "product_status": "Active"
      }
      const fetchProductList = await jwtFetch(config.ENDPOINT + 'get/product', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestProductList)
      });
      const responseProductList = await fetchProductList.json();
      if (responseProductList.status === 200) {
        setProductList(responseProductList.data.filter(product => product.product_status === 'Active'));
      } else {
        alert(responseProductList.message);
      }
    } catch (error) {
      console.error('Error in getProductList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getCategoryList = async () => {
    try {
      const requestCategoryList = {
        "product_category_status": "Active"
      }
      const fetchCategoryList = await jwtFetch(config.ENDPOINT + 'get/product_category', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCategoryList)
      });
      const responseCategoryList = await fetchCategoryList.json();
      if (responseCategoryList.status === 200) {
        for (let i in responseCategoryList.data) {
          responseCategoryList.data[i].value = responseCategoryList.data[i].product_category_terms;
          responseCategoryList.data[i].label = responseCategoryList.data[i].product_category_name;
        }
        setCategoryList(responseCategoryList.data.filter(category => category.product_category_terms !== null).sort(category => (category.label || '').localeCompare(category.label || '')));
      } else {
        alert(responseCategoryList.message);
      }
    } catch (error) {
      console.error('Error in getCategoryList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const changeSubscriptionTerm = () => {
    if (subscriptionTerm) {
      setQuotationSubscriptionTerm(subscriptionTerm.value);
    }
    setSubscriptionTerm(null);
  }

  const changePaymentTerm = () => {
    if (paymentTerm) {
      setQuotationPaymentTerm(paymentTerm.value);
    }
    setPaymentTerm(null);
  }

  const changeCancellationTerm = () => {
    if (cancellationTerm) {
      setQuotationCancellationTerm(cancellationTerm.value);
    }
    setCancellationTerm(null);
  }

  const getPaymentSimulationList = async () => {
    try {
      const fetchPaymentSimulationList = await jwtFetch(config.ENDPOINT + 'get/quotation_payment_simulation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quotation_id: id_quotation
        })
      });
      const responsePaymentSimulationList = await fetchPaymentSimulationList.json();
      if (responsePaymentSimulationList.status === 200) {
        setPaymentSimulationList(responsePaymentSimulationList.data);
      } else {
        alert(responsePaymentSimulationList.message);
      }
    } catch (error) {
      console.error('Error in getPaymentSimulationList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getCompanyLicence = async (productId) => {
    try {
      const requestCompanyLicence = {
        "company_id": companyId.value,
        "product_id": productId
      }
      const fetchCompanyLicence = await jwtFetch(config.ENDPOINT + 'get/clients_company_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyLicence)
      });
      const responseCompanyLicence = await fetchCompanyLicence.json();
      if (responseCompanyLicence.status === 200) {
        if (responseCompanyLicence.data.length > 0) {
          return responseCompanyLicence.data[0];
        } else {
          return null;
        }
      } else {
        alert(responseCompanyLicence.message);
      }
    } catch (error) {
      console.error('Error in getCompanyLicence:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }
  
  const getCompanyDomains = async () => {
    try {
      const requestCompanyLicence = {
        "company_id": companyId.value,
      }
      const fetchCompanyLicence = await jwtFetch(config.ENDPOINT + 'get/clients_company_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyLicence)
      });
      const responseCompanyLicence = await fetchCompanyLicence.json();
      if (responseCompanyLicence.status === 200) {
        if (responseCompanyLicence.data.length > 0) {
          const company = companyList.find(company => company.id_company === companyId.value);
          let domains = [{
            domain: company.company_domain,
            product_id: null,
          }];
          for (let i in responseCompanyLicence.data) {
            domains.push({
              domain: responseCompanyLicence.data[i].licence_domain,
              product_id: responseCompanyLicence.data[i].product_id,
            });
          }
          setDomainList(domains);
        } else {
          return null;
        }
      } else {
        alert(responseCompanyLicence.message);
      }
    } catch (error) {
      console.error('Error in getCompanyDomains:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getAdditionalLicense = async (requestAdditionalLicense) => {
    try {
      const fetchAdditionalLicense = await jwtFetch(config.ENDPOINT + 'simulation/additional_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestAdditionalLicense)
      });
      const responseAdditionalLicense = await fetchAdditionalLicense.json();
      if (responseAdditionalLicense.status === 200) {
        if (responseAdditionalLicense.data.length > 0) {
          return responseAdditionalLicense.data;
        } else {
          return null;
        }
      } else {
        alert(responseAdditionalLicense.message);
      }
    } catch (error) {
      console.error('Error in getAdditionalLicense:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const addQuotationBundle = () => {
    const quotationBundle = {
      bundle_code: '',
      bundle_display_name: '',
      bundle_display_description: '',
      bundle_qty: 1,
      bundle_subtotal: 0,
      bundle_items: [],
    };
    setQuotationBundleList([...quotationBundleList, quotationBundle]);
  }

  const changeQuotationBundleData = (column, value, index) => {
    const quotationBundle = quotationBundleList[index];
    quotationBundle[column] = value;
    if (column === 'bundle_display_name') {
      quotationBundle.bundle_code = slugFormat(value);
    }
    setQuotationBundleList([...quotationBundleList]);
  }

  const changeQuotationBundleItem = (bundleItems, index) => {
    let subtotal = 0;
    for (let i in bundleItems) {
      subtotal += parseFloat(bundleItems[i].total);
    }
    let quotationBundles = quotationBundleList;
    quotationBundles[index].bundle_items = bundleItems;
    quotationBundles[index].bundle_subtotal = subtotal;
    setQuotationBundleList([...quotationBundles]);
  }

  const removeQuotationBundle = (index) => {
    quotationBundleList.splice(index, 1);
    setQuotationBundleList([...quotationBundleList]);
  }

  const addQuotationItem = () => {
    const quotationItem = {
      product: {},
      product_id: '',
      product_name: '',
      product_display_name: '',
      product_display_description: '',
      product_type: '',
      product_category: '',
      product_domain: domainList.length > 0 ? domainList[0].domain : '',
      product_domain_new: '',
      product_currency_original: '',
      product_currency: quotationCurrency,
      product_cost_original: 0,
      product_cost: 0,
      product_price_original: 0,
      product_price: 0,
      product_qty: 1,
      product_subtotal: 0,
      product_discount_price: 0,
      product_discount_rate: 0,
      product_total: 0,
      product_margin_price: 0,
      product_margin_rate: 0,
      product_tagging: '',
      service_type: 'New Order',
      is_upgrade: false,
      contract_long_period: 12,
      contract_duration_period: 'Month',
    };
    setQuotationItemList([...quotationItemList, quotationItem]);
  }

  const changeQuotationItemProduct = async (productId, index) => {
    const product = productList.find(product => {
      return parseInt(product.id_product) === parseInt(productId.value);
    });
    const quotationItem = quotationItemList[index];
    let contract_duration = 0;
    const companyLicense = await getCompanyLicence(productId.value);
    if (companyLicense) {
      setHasCompanyLicense(true);
      quotationItem.service_type = 'Additional';
      quotationItem.company_licence = companyLicense;
      quotationItem.product_active_licence_date = companyLicense.licence_start_period;
      product.product_price = companyLicense.licence_price_seat;
      setQuotationBillingPaymentTerm(companyLicense.billcycle_payment_term);
      setQuotationBillingPaymentPeriod(companyLicense.billcycle_payment_period);
      setActiveLicenceDate(companyLicense.licence_start_period);
      const additionalLicense = await getAdditionalLicense({
        "subscription": {
          "item_name": product.product_name,
          "sku_price": product.product_price,
          "num_user": companyLicense.total_licences,
          "product_discount_price": quotationItem.product_discount_price,
          "product_discount_prorate": 0,
          "billing_start_date": format(new Date(companyLicense.licence_start_period), 'dd MMM yyyy'),
          "installment_type": companyLicense.billcycle_payment_period,
          "total_contract_month": companyLicense.licence_long_period,
          "quotation_tax_rate": 12,
        },
        "additionalLicences": {
            "additional_licence_start_date": format(new Date(quotationCreatedDate), 'dd MMM yyyy'),
            "additional_licence_sku_price": product.product_price,
            "additional_licence_num_user": quotationItem.product_qty,
            "additional_licence_idr_usd_rate": quotationExchangeRate,
            "additional_licence_product_discount_price": quotationItem.product_discount_price,
            "additional_licence_product_discount_prorate": 0,
        },
      });
      if (additionalLicense) {
        for (let a in additionalLicense) {
          contract_duration += additionalLicense[a].number_of_months;
        }
        quotationItem.contract_long_period = contract_duration;
      }      
    } else {
      const anyAdditional = quotationItemList.some(item => item.service_type === 'Additional');
      if (!anyAdditional) {
        setQuotationBillingPaymentTerm('Full Payment');
        setQuotationBillingPaymentPeriod('One Time');
      }
      setActiveLicenceDate(null);
      setHasCompanyLicense(false);
      quotationItem.service_type = 'New Order';
      quotationItem.company_licence = null;
      quotationItem.product_active_licence_date = null;
      if (product.product_type === 'Subscription') {
        quotationItem.contract_long_period = 12;
      } else {
        quotationItem.contract_long_period = 1;
      }
    }

    quotationItem.product = productId;
    quotationItem.product_id = product.id_product;
    quotationItem.product_name = product.product_name;
    quotationItem.product_display_name = product.product_name;
    quotationItem.product_display_description = product.product_description;
    quotationItem.product_display_description = '';
    quotationItem.product_type = product.product_type;
    quotationItem.product_category = product.product_category_id;
    quotationItem.product_currency_original = product.product_currency;
    quotationItem.product_cost_original = product.product_cost;
    quotationItem.product_price_original = product.product_price;
    const { product_cost, product_price } = applyCurrencyConversion(quotationItem, quotationCurrency, quotationExchangeRate);
    quotationItem.product_cost = product_cost;
    quotationItem.product_price = product_price;
    Object.assign(quotationItem, recalculateItemTotals(quotationItem));
    setQuotationItemList([...quotationItemList]);
  }

  const changeQuotationItemData = (column, value, index) => {
    const quotationItem = quotationItemList[index];
    quotationItem[column] = value;
    if (column === 'product_cost_original') {
      quotationItem.product_cost = parseFloat((quotationItem.product_cost_original * quotationExchangeRate).toFixed(3));
    } else if (column === 'product_price_original') {
      quotationItem.product_price = parseFloat((quotationItem.product_price_original * quotationExchangeRate).toFixed(3));
    } else if (column === 'product_discount_price') {
      quotationItem.product_discount_rate = parseFloat((quotationItem.product_discount_price / quotationItem.product_subtotal * 100).toFixed(3));
    } else if (column === 'product_discount_rate') {
      quotationItem.product_discount_price = parseFloat((quotationItem.product_subtotal * quotationItem.product_discount_rate / 100).toFixed(3));
    }
    Object.assign(quotationItem, recalculateItemTotals(quotationItem));
    setQuotationItemList([...quotationItemList]);
  }

  const removeQuotationItem = (index) => {
    quotationItemList.splice(index, 1);
    setQuotationItemList([...quotationItemList]);
  }

  const changeQuotationDiscount = (column, value) => {
    if (column === 'quotation_discount_rate') {
      setQuotationDiscountRate(value);
      setQuotationDiscountPrice(parseFloat((quotationSubtotal * value / 100).toFixed(3)));
    } else if (column === 'quotation_discount_price') {
      setQuotationDiscountRate(parseFloat((value / quotationSubtotal * 100).toFixed(3)));
      setQuotationDiscountPrice(value);
    }
  }

  const addPayment = () => {
    const payment = {
      number_of_months: 1,
      start_term_period: paymentList ? format(new Date(), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      end_term_period: null,
      payment_amount_exc_tax: 0,
      payment_amount_inc_tax: 0,
      payment_rate: 0,
      error_rate: true,
      error_date: true
    }
    setPaymentList([...paymentList, payment]);
  }

  const changePaymentData = (column, value, index) => {
    const payment = paymentList[index];
    payment[column] = value;
    if (column === 'payment_rate') {
      payment.payment_amount_exc_tax = parseFloat((quotationTotal * value / 100).toFixed(3));
    } else if (column === 'payment_amount_exc_tax') {
      payment.payment_rate = parseFloat((value / quotationTotal * 100).toFixed(3));
    }
    setPaymentList([...paymentList]);
  }

  const removePayment = (index) => {
    paymentList.splice(index, 1);
    setPaymentList([...paymentList]);
  }

  const changeQuotationPaymentOption = (index, value) => {
    let quotationItem = quotationItemList[index];
    quotationItem.contract_long_period = value * quotationBillingLongPeriod;
    quotationItem.product_price = quotationItem.product_price / (value * quotationBillingLongPeriod);
    setQuotationPaymentOption(value);
    setQuotationItemList([...quotationItemList]);
  }

  const submitQuotationEdit = async () => {
    try {
      setIsLoadingSubmit(true);

      let errorSubmit = null;
      if (paymentList.some(payment => payment.error_rate)) {
        errorSubmit = 'Total payment percentage must be 100%';
      }
      //if (quotationMarginTotal < 0) {
        //errorSubmit = 'Total margin is less than 0';
      //}
      if (quotationCancellationTerm === '') {
        errorSubmit = 'Cancellation term is required';
      }
      if (quotationPaymentTerm === '') {
        errorSubmit = 'Payment term is required';
      }    
      if (quotationItemList.some(item => item.product_type === 'Subscription')) {
        if (quotationSubscriptionTerm === '') {
          errorSubmit = 'Subscription term is required';
        }
      }
      if (quotationItemList.some(item => !item.product_tagging || item.product_tagging === '')) {
        errorSubmit = 'Product tagging is required for all items';
      }
      if (errorSubmit) {
        alert(errorSubmit);
        setIsLoadingSubmit(false);
        return;
      }
      const crmUser = JSON.parse(localStorage.getItem('crmUser'));
      const quotationItems = [];
      const quotationItemLists = [];
      const quotationCustomPayment = [];

      for (let i in quotationBundleList) {
        const productProrate = parseFloat((quotationBundleList[i].bundle_subtotal / quotationSubtotal).toFixed(3));
        const discountProrate = parseFloat((quotationDiscountPrice * productProrate).toFixed(3));
        quotationItems.push({
          "bundle_code": quotationBundleList[i].bundle_code,
          "product_display_name": quotationBundleList[i].bundle_display_name,
          "product_display_description": quotationBundleList[i].bundle_display_description,
          "product_type": "Bundle",
          "product_domain": quotationItemList[0].product_domain,
          "product_qty": 1,
          "product_currency_original": quotationCurrency,
          "product_currency": quotationCurrency,
          "product_cost_original": 0,
          "product_cost": 0,
          "product_price_original": parseFloat((quotationBundleList[i].bundle_subtotal / quotationItemList[0].contract_long_period).toFixed(3)),
          "product_price": parseFloat((quotationBundleList[i].bundle_subtotal / quotationItemList[0].contract_long_period).toFixed(3)),
          "product_subtotal": quotationBundleList[i].bundle_subtotal,
          "product_discount_price": 0,
          "product_discount_rate": 0,
          "product_discount_prorate": discountProrate,
          "product_margin_price": 0,
          "product_margin_rate": 0,
          "product_total": quotationBundleList[i].bundle_subtotal,
          "product_grand_total": quotationBundleList[i].bundle_subtotal,
          "product_active_licence_date": quotationItemList[0].product_active_licence_date ? format(quotationItemList[0].product_active_licence_date, 'yyyy-MM-dd') : null,
          "product_tagging": quotationItemList[0].product_tagging,
          "service_type": 'New Order',
          "is_upgrade": quotationItemList[0].is_upgrade,
          "contract_long_period": quotationItemList[0].contract_long_period,
          "contract_duration_period": 'Month',
          "billing_long_period": quotationBillingLongPeriod,
          "billing_duration_period": 'Month',
          "billing_payment_term": quotationBillingPaymentTerm,
          "billing_payment_period": quotationBillingPaymentPeriod,
          "action": 'Update',
        });
      }

      for (let i in quotationItemList) {
        const productProrate = parseFloat((quotationItemList[i].product_total / quotationSubtotal).toFixed(3));
        const discountProrate = parseFloat((quotationDiscountPrice * productProrate).toFixed(3));
        const grandTotal = quotationItemList[i].product_total - discountProrate;
        if (quotationItemList[i].product_id) {
          let bundleCode = null;
          for (let j in quotationBundleList) {
            if (quotationBundleList[j].bundle_items.find((product) => product.value === quotationItemList[i].product_id)) {
              bundleCode = quotationBundleList[j].bundle_code;
            }
          }
          if (!bundleCode) {
            quotationItems.push({
              "bundle_code": bundleCode,
              "product_id": quotationItemList[i].product_id,
              "product_display_name": quotationItemList[i].product_display_name,
              "product_display_description": quotationItemList[i].product_display_description,
              "product_type": quotationItemList[i].product_type,
              "product_domain": quotationItemList[i].product_domain,
              "product_qty": quotationItemList[i].product_qty,
              "product_currency_original": quotationItemList[i].product_currency_original,
              "product_currency": quotationCurrency,
              "product_cost_original": quotationItemList[i].product_cost_original,
              "product_cost": quotationItemList[i].product_cost,
              "product_price_original": quotationItemList[i].product_price_original,
              "product_price": quotationItemList[i].product_price,
              "product_subtotal": quotationItemList[i].product_subtotal,
              "product_discount_price": quotationItemList[i].product_discount_price,
              "product_discount_rate": quotationItemList[i].product_discount_rate,
              "product_discount_prorate": discountProrate,
              "product_margin_price": quotationItemList[i].product_margin_price,
              "product_margin_rate": quotationItemList[i].product_margin_rate,
              "product_total": quotationItemList[i].product_total,
              "product_grand_total": grandTotal,
              "product_active_licence_date": quotationItemList[i].product_active_licence_date ? format(quotationItemList[i].product_active_licence_date, 'yyyy-MM-dd') : null,
              "product_tagging": quotationItemList[i].product_tagging,
              "service_type": quotationItemList[i].service_type,
              "is_upgrade": quotationItemList[i].is_upgrade,
              "contract_long_period": quotationItemList[i].contract_long_period,
              "contract_duration_period": 'Month',
              "billing_long_period": quotationBillingLongPeriod,
              "billing_duration_period": 'Month',
              "billing_payment_term": quotationBillingPaymentTerm,
              "billing_payment_period": quotationBillingPaymentPeriod,
              "action": 'Update',
            });
          }
          quotationItemLists.push({
            "bundle_code": bundleCode,
            "product_id": quotationItemList[i].product_id,
            "product_display_name": quotationItemList[i].product_display_name,
            "product_display_description": quotationItemList[i].product_display_description,
            "product_type": quotationItemList[i].product_type,
            "product_domain": quotationItemList[i].product_domain,
            "product_qty": quotationItemList[i].product_qty,
            "product_currency_original": quotationItemList[i].product_currency_original,
            "product_currency": quotationCurrency,
            "product_cost_original": quotationItemList[i].product_cost_original,
            "product_cost": quotationItemList[i].product_cost,
            "product_price_original": quotationItemList[i].product_price_original,
            "product_price": quotationItemList[i].product_price,
            "product_subtotal": quotationItemList[i].product_subtotal,
            "product_discount_price": quotationItemList[i].product_discount_price,
            "product_discount_rate": quotationItemList[i].product_discount_rate,
            "product_discount_prorate": discountProrate,
            "product_margin_price": quotationItemList[i].product_margin_price,
            "product_margin_rate": quotationItemList[i].product_margin_rate,
            "product_total": quotationItemList[i].product_total,
            "product_grand_total": grandTotal,
            "product_active_licence_date": quotationItemList[i].product_active_licence_date ? format(quotationItemList[i].product_active_licence_date, 'yyyy-MM-dd') : null,
            "product_tagging": quotationItemList[i].product_tagging,
            "service_type": quotationItemList[i].service_type,
            "is_upgrade": quotationItemList[i].is_upgrade,
            "contract_long_period": quotationItemList[i].contract_long_period,
            "contract_duration_period": 'Month',
            "billing_long_period": quotationBillingLongPeriod,
            "billing_duration_period": 'Month',
            "billing_payment_term": quotationBillingPaymentTerm,
            "billing_payment_period": quotationBillingPaymentPeriod,
            "action": 'Update',
          });
        }
      }
      if (quotationBillingPaymentPeriod === 'Custom') {
        for (let i in paymentList) {
          quotationCustomPayment.push({
            "term_number": "Term " + (parseInt(i) + 1),
            "number_of_months": 1,
            "start_term_period": format(paymentList[i].start_term_period, 'yyyy-MM-dd 00:00:00'),
            "end_term_period": format(paymentList[i].start_term_period, 'yyyy-MM-dd 23:59:59'),
            "payment_amount_exc_tax": paymentList[i].payment_amount_exc_tax,
            "payment_amount_inc_tax": paymentList[i].payment_amount_inc_tax,
            "payment_rate": paymentList[i].payment_rate,
            "action": 'Update',
          })
        }
      }
      const requestQuotationEdit = {
        "id_quotation": id_quotation,
        "company_id": companyId.value,
        "subcompany_id": subcompanyId ? subcompanyId.value : null,
        "quotation_currency": quotationCurrency,
        "quotation_exchange_rate": quotationExchangeRate,
        "quotation_exchange_floating": quotationExchangeFloating ? 'Floating' : 'Flat Rate',
        "quotation_expired_date": quotationExpiredDate,
        "quotation_subtotal": quotationSubtotal,
        "quotation_discount_rate": quotationDiscountRate || 0,
        "quotation_discount_price": quotationDiscountPrice || 0,
        "quotation_cost": quotationCostTotal,
        "quotation_margin_rate": quotationMarginRate,
        "quotation_margin_price": quotationMarginTotal,
        "quotation_total": quotationTotal,
        "quotation_approval": "Draft",
        "quotation_status": "Draft",
        "subscription_terms": quotationSubscriptionTerm,
        "payment_terms": quotationPaymentTerm,
        "cancellation_terms": quotationCancellationTerm,
        "company_address": quotationCompanyAddress,
        "created_on": quotationCreatedDate ? format(quotationCreatedDate, 'yyyy-MM-dd HH:mm:ss') : format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        "updated_on": format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        "updated_by": crmUser.user_keyid,
        "quotation_items": quotationItems,
        "quotation_item_list": quotationItemLists,
        "quotation_custom_payment": quotationCustomPayment,
        "is_invoicing_only": isInvoicingOnly,
        "action": 'Update',
      };

      const fetchQuotationEdit = await jwtFetch(config.ENDPOINT + 'update/quotation/new', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestQuotationEdit)
      });
      const responseQuotationEdit = await fetchQuotationEdit.json();
      if (responseQuotationEdit.status === 200) {
        window.location.href = '/quotation/detail/' + id_quotation;
      } else {
        alert(responseQuotationEdit.message);
        setIsLoadingSubmit(false);
      }
    } catch (error) {
      console.error('Error in submitQuotationEdit:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  }

  return (
    <div id="quotation-edit">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="quotation"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="main">
              <div className="p-4">
                
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="fs-24"><button className="btn btn-link" onClick={() => navigate(-1)}><i className="bi bi-arrow-left"/></button> Quotation Edit</div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Company Group <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <Select value={companyId} isDisabled required />
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Subsidiary <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <Select value={subcompanyId} isDisabled required />
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Currency <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <select className="form-select" value={quotationCurrency} onChange={(e) => setQuotationCurrency(e.target.value)} disabled>
                            <option value="USD">USD</option>
                            <option value="IDR">IDR</option>
                            <option value="SGD">SGD</option>
                            <option value="MYR">MYR</option>
                          </select>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Exchange Rate <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-4">
                          <div className="input-group mb-2">
                            <input className="form-control text-end" type="text" value={1} disabled required/>
                            <span className="input-group-text">USD</span>
                          </div>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <input className="form-check-input" type="radio" name="exchange-options" id="option-floating" checked={quotationExchangeFloating === true} onChange={() => setQuotationExchangeFloating(true)}/>
                              <label className="form-check-label" htmlFor="option-floating">
                                Floating
                              </label>
                            </div>
                            <div className="form-check">
                              <input className="form-check-input" type="radio" name="exchange-options" id="option-flat" checked={quotationExchangeFloating === false} onChange={() => setQuotationExchangeFloating(false)}/>
                              <label className="form-check-label" htmlFor="option-flat">
                                Flat
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="input-group mb-1">
                            <input className="form-control text-end" type="text" value={priceFormat(quotationExchangeRate)} onChange={(e) => setQuotationExchangeRate(numberFormat(e.target.value))} disabled/>
                            <span className="input-group-text">{quotationCurrency}</span>
                          </div>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Effective Date <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <input className="form-control" type="date" value={quotationCreatedDate} onChange={(e) => setQuotationCreatedDate(e.target.value)} disabled/>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Expiry Date <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <input className="form-control" type="date" value={quotationExpiredDate} onChange={(e) => setQuotationExpiredDate(e.target.value)} required/>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group">
                        <div className="col-md-4">
                          <label>Is it for invoicing only?</label>
                        </div>
                        <div className="col-md-8">
                          <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" id="is-invoicing-only" onChange={() => setIsInvoicingOnly(!isInvoicingOnly)} checked={isInvoicingOnly}/>
                            <label className="form-check-label" htmlFor="is-invoicing-only">{isInvoicingOnly ? 'Yes' : 'No'}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {subcompanyId && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">SKU List</div>
                    </div>
                    {quotationItemList.map((item, i) =>
                      <div className="card mb-3" key={"item-" + i}>
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="fs-18"><strong>SKU #{i + 1}</strong></div>
                            <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => removeQuotationItem(i)}><i className="bi bi-trash me-2"/> Remove</button>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Internal Product Name <span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <Select value={item.product} options={productList && [...productList].sort((a, b) => (a.product_name || '').localeCompare(b.product_name || '')).map(product => ({ value: product.id_product, label: product.product_name }))} onChange={(value) => changeQuotationItemProduct(value, i)} isDisabled={true} required />
                                </div>
                              </div>
                              {item.product_id && (
                                <div>
                                  <div className="row align-items-baseline form-group mb-2">
                                    <div className="col-md-4">
                                      <label>Display Name in QT<span className="text-danger">*</span></label>
                                    </div>
                                    <div className="col-md-8">
                                      <input className="form-control" type="text" value={item.product_display_name} onChange={(e) => changeQuotationItemData('product_display_name', e.target.value, i)} required/>
                                    </div>
                                  </div>
                                  <div className="row align-items-baseline form-group mb-2">
                                    <div className="col-md-4">
                                      <label>Display Description <span className="text-danger">*</span></label>
                                    </div>
                                    <div className="col-md-8">
                                      <textarea className="form-control" rows="4" value={item.product_display_description} onChange={(e) => changeQuotationItemData('product_display_description', e.target.value, i)} required/>
                                    </div>
                                  </div>
                                  {(quotationItemList.some(item => item.service_type === 'Additional') && item.service_type !== 'Additional') ? (
                                    <>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Payment Option <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <select className="form-select" value={quotationPaymentOption} onChange={(e) => changeQuotationPaymentOption(i, e.target.value)} required>
                                            <option value=""></option>
                                            <option value="1">1 x 100%</option>
                                            <option value="2" disabled>2 x 50%</option>
                                          </select>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Quantity <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_qty)} onChange={(e) => changeQuotationItemData('product_qty', numberFormat(e.target.value), i)} required/>
                                            <span className="input-group-text mw-85">Unit(s)</span>
                                          </div>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Domain for Provisioning<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <input className="form-control" type="text" value={item.product_domain} disabled required/>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Order Type <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <input className="form-control" type="text" value={item.service_type} disabled/>
                                          <select className="d-none form-select" value={item.service_type} onChange={(e) => changeQuotationItemData('service_type', e.target.value, i)} disabled={!(item.company_licence)} required>
                                            {!(item.company_licence) && (<option value="New Order">New Order</option>)}
                                            {item.company_licence && item.company_licence.licence_status === 'Active' && (<option value="Additional">Additional</option>)}
                                            {item.company_licence && item.company_licence.licence_status === 'Active' && (<option value="Renewal">Renewal</option>)}
                                          </select>
                                          {item.company_licence && (
                                            <div className="card mt-2">
                                              <div className="card-body">
                                                <div className="row align-items-baseline form-group mb-2">
                                                  <div className="col-md-6">Subscription Status</div>
                                                  <div className="col-md-6">{item.company_licence.licence_status}</div>
                                                </div>
                                                <div className="row align-items-baseline form-group mb-2">
                                                  <div className="col-md-6">Active Date</div>
                                                  <div className="col-md-6">{format(new Date(item.company_licence.licence_start_period), 'dd MMMM yyyy')}</div>
                                                </div>
                                                <div className="row align-items-baseline form-group">
                                                  <div className="col-md-6">Expired Date</div>
                                                  <div className="col-md-6">{format(new Date(item.company_licence.licence_end_period), 'dd MMMM yyyy')}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Is it upgrade?</label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id={"is-upgrade-" + i} onChange={() => changeQuotationItemData('is_upgrade', !item.is_upgrade, i)} checked={item.is_upgrade}/>
                                            <label className="form-check-label" htmlFor={"is-upgrade-" + i}>{item.is_upgrade ? 'Yes' : 'No'}</label>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Contract Period/Multipliers <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.contract_long_period)} onChange={(e) => changeQuotationItemData('contract_long_period', numberFormat(e.target.value), i)} disabled={item.service_type === 'Additional'} required/>
                                            <span className="input-group-text mw-85">Month(s)</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Quantity <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_qty)} onChange={(e) => changeQuotationItemData('product_qty', numberFormat(e.target.value), i)} disabled/>
                                            <span className="input-group-text mw-85">Unit(s)</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Tagging <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <select className="form-select" value={item.product_tagging} onChange={(e) => changeQuotationItemData('product_tagging', e.target.value, i)} required>
                                            <option value=""></option>
                                            {productTaggingOptions.map((item, index) => (
                                              <option key={index} value={item.value}>{item.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6">
                              {item.product_id && (
                                <div>
                                  <div className="card mb-2">
                                    <div className="card-body">
                                      {item.product_currency_original === item.product_currency ? (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Cost Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-8">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_cost)} onChange={(e) => changeQuotationItemData('product_cost', numberFormat(e.target.value), i)} disabled/>
                                              <span className="input-group-text">{item.product_currency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Cost Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-3">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_cost_original, '', true)} onChange={(e) => changeQuotationItemData('product_cost_original', numberFormat(e.target.value), i)} disabled/>
                                              <span className="input-group-text">{item.product_currency_original}</span>
                                            </div>
                                          </div>
                                          <div className="col-md-5">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_cost)} required disabled/>
                                              <span className="input-group-text">{quotationCurrency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Subtotal of Cost Price<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(parseFloat((item.product_cost * item.product_qty * item.contract_long_period).toFixed(3)))} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card mb-2">
                                    <div className="card-body">
                                      {item.product_currency_original === item.product_currency ? (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Selling Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-8">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_price)} onChange={(e) => changeQuotationItemData('product_price', numberFormat(e.target.value), i)} disabled/>
                                              <span className="input-group-text">{item.product_currency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Selling Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-3">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_price_original, '', true)} onChange={(e) => changeQuotationItemData('product_price_original', numberFormat(e.target.value), i)} disabled/>
                                              <span className="input-group-text">{item.product_currency_original}</span>
                                            </div>
                                          </div>
                                          <div className="col-md-5">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_price)} required disabled/>
                                              <span className="input-group-text">{quotationCurrency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Subtotal of this SKU<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_subtotal)} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card mb-2">
                                    <div className="card-body">
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Lumpsum Discount on this SKU</label>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_discount_rate)} onChange={(e) => changeQuotationItemData('product_discount_rate', numberFormat(e.target.value), i)} disabled/>
                                            <span className="input-group-text">%</span>
                                          </div>
                                        </div>
                                        <div className="col-md-5">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_discount_price)} onChange={(e) => changeQuotationItemData('product_discount_price', numberFormat(e.target.value), i)} disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Total of this SKU<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_total)} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card">
                                    <div className="card-body">
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Margin Amount<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_margin_rate)} required disabled/>
                                            <span className="input-group-text">%</span>
                                          </div>
                                        </div>
                                        <div className="col-md-5">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_margin_price)} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {quotationBundleList.map((bundle, i) =>
                      <div className="card mb-3" key={"bundle-" + i}>
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="fs-18"><strong>Bundle #{i + 1}</strong></div>
                            <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => removeQuotationBundle(i)}><i className="bi bi-trash me-2"/> Remove</button>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Bundle Name<span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <input className="form-control" type="text" value={bundle.bundle_display_name} onChange={(e) => changeQuotationBundleData('bundle_display_name', e.target.value, i)} required/>
                                </div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Bundle Description <span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <textarea className="form-control" rows="4" value={bundle.bundle_display_description} onChange={(e) => changeQuotationBundleData('bundle_display_description', e.target.value, i)} required/>
                                </div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>SKUs <span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <Select value={bundle.bundle_items} options={quotationItemList && quotationItemList.map(product => ({ value: product.product_id, label: product.product_display_name, total: product.product_total }))} onChange={(value) => changeQuotationBundleItem(value, i)} isDisabled={quotationItemList.length === 0} isMulti required />
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group">
                                <div className="col-md-4">
                                  <label>Subtotal<span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <div className="input-group">
                                    <input className="form-control text-end" type="text" value={priceFormat(bundle.bundle_subtotal)} required disabled/>
                                    <span className="input-group-text">{quotationCurrency}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* <div className="d-flex">
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={addQuotationItem}><i className="bi bi-plus-lg me-2"/> SKU</button>
                      <button className="btn btn-sm btn-outline-secondary ms-2" type="button" onClick={addQuotationBundle}><i className="bi bi-plus-lg me-2"/> Bundle</button>
                    </div> */}
                  </div>
                )}
                
                {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Summary</div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Subtotal of all Items<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationSubtotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Lumpsum Discount for this QT</label>
                          </div>
                          <div className="col-md-3">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationDiscountRate)} onChange={(e) => changeQuotationDiscount('quotation_discount_rate', numberFormat(e.target.value))} disabled/>
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationDiscountPrice)} onChange={(e) => changeQuotationDiscount('quotation_discount_price', numberFormat(e.target.value))} disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Total Before Tax<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationTotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Total Cost Price for this QT<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationCostTotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Total Gross Margin<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-3">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationMarginRate)} required disabled/>
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationMarginTotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Payment</div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Payment Schedule <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <select className="form-select" value={quotationBillingPaymentTerm} onChange={(e) => setQuotationBillingPaymentTerm(e.target.value)} required>
                              <option value="Full Payment">Full Payment</option>
                              <option value="Installment">Installment</option>
                            </select>
                          </div>
                        </div>
                        {quotationBillingPaymentTerm === 'Installment' && (
                          <div className="row align-items-baseline form-group mb-2">
                            <div className="col-md-4">
                              <label>Installment Schedule <span className="text-danger">*</span></label>
                            </div>
                            <div className="col-md-8">
                              <select className="form-select" value={quotationBillingPaymentPeriod} onChange={(e) => setQuotationBillingPaymentPeriod(e.target.value)} disabled={quotationItemList.some(item => item.service_type === 'Additional')} required>
                                <option value="Monthly">Monthly</option>
                                <option value="Quarterly">Quarterly</option>
                                <option value="Semesterly">Semesterly</option>
                                <option value="Annually">Annually</option>
                                <option value="Custom">Custom</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                      {paymentList && paymentList.length > 0 && (
                        <div className="col-md-10 offset-md-2">
                          <div className="table-responsive">
                            <table className="table table-bordered mb-2">
                              <thead>
                                <tr>
                                  <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Schedules</th>
                                  <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Invoice Date</th>
                                  <th className="bg-secondary text-light w-200" style={{textAlign: 'center'}}>Percentage</th>
                                  <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Amount</th>
                                  <th className="bg-secondary text-light"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {paymentList.map((payment, p) =>
                                  <tr key={"payment-" + p}>
                                    <td align="center" valign="middle">Schedule {p + 1}</td>
                                    <td><input className={"form-control " + (payment.error_date ? 'border-danger' : '')} type="date" value={payment.start_term_period} onChange={(e) => changePaymentData('start_term_period', e.target.value, p)} required/></td>
                                    <td>
                                      <div className="input-group">
                                        <input className={"form-control text-end " + (payment.error_rate ? 'border-danger' : '')} type="text" value={priceFormat(payment.payment_rate, '', true)} onChange={(e) => changePaymentData('payment_rate', numberFormat(e.target.value), p)} required/>
                                        <span className="input-group-text">%</span>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="input-group">
                                        <input className="form-control text-end" type="text" value={priceFormat(payment.payment_amount_exc_tax)} onChange={(e) => changePaymentData('payment_amount_exc_tax', numberFormat(e.target.value), p)} required/>
                                        <span className="input-group-text">{quotationCurrency}</span>
                                      </div>
                                    </td>
                                    <td align="center">
                                      <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removePayment(p)} disabled={paymentList.length === 1}><i className="bi bi-trash"/></button>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="d-flex justify-content-start">
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addPayment()}><i className="bi bi-plus-lg me-2"/> Schedule</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Terms &amp; Conditions</div>
                    </div>
                    {quotationItemList.some(item => item.product_type === 'Subscription') && (
                      <div className="row mb-4">
                        <div className="col-md-12">
                          <div className="row align-items-baseline form-group mb-2">
                            <div className="col-md-2">
                              <label>Subscription Terms <span className="text-danger">*</span></label>
                            </div>
                            <div className="col-md-10">
                              <textarea className="form-control mb-2" rows="8" value={quotationSubscriptionTerm} onChange={(e) => setQuotationSubscriptionTerm(e.target.value)} required/>
                              <div className="d-flex align-items-center">
                                <Select className="w-400" value={subscriptionTerm} options={categoryList} placeholder="Select Terms Template..." onChange={(value) => setSubscriptionTerm(value)}/>
                                <button className="btn btn-outline-secondary ms-2" type="button" onClick={() => changeSubscriptionTerm()}>Insert</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Payment Terms <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            <textarea className="form-control mb-2" rows="8" value={quotationPaymentTerm} onChange={(e) => setQuotationPaymentTerm(e.target.value)} required/>
                            <div className="d-flex align-items-center">
                              <Select className="w-400" value={paymentTerm} options={paymentTermOptions} placeholder="Select Terms Template..." onChange={(value) => setPaymentTerm(value)}/>
                              <button className="btn btn-outline-secondary ms-2" type="button" onClick={() => changePaymentTerm()}>Insert</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Cancellation Terms <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            <textarea className="form-control mb-2" rows="8" value={quotationCancellationTerm} onChange={(e) => setQuotationCancellationTerm(e.target.value)} required/>
                            <div className="d-flex align-items-center">
                              <Select className="w-400" value={cancellationTerm} options={cancellationTermOptions} placeholder="Select Terms Template..." onChange={(value) => setCancellationTerm(value)}/>
                              <button className="btn btn-outline-secondary ms-2" type="button" onClick={() => changeCancellationTerm()}>Insert</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="d-flex justify-content-end">
                    <a className="btn btn-link" href={"/quotation/detail/" + id_quotation}>Cancel</a>
                    <button className="btn btn-primary ms-2" type="button" onClick={() => submitQuotationEdit()} disabled={isLoadingSubmit}>{isLoadingSubmit ? 'Loading...' : 'Save'}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationEdit;


